import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Check, X, ShieldCheck, ClipboardCheck, Loader2, Inbox } from "lucide-react";
import { toast } from "sonner";
import { compromisoApi, apiError } from "../lib/api";

export default function Validaciones() {
  const navigate = useNavigate();
  const [data, setData] = useState({ actividades: [], hojas: [] });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    try { setData(await compromisoApi.parentPending()); } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); const iv = setInterval(load, 5000); return () => clearInterval(iv); }, [load]);

  const validate = async (id, approve) => {
    setBusy(id);
    try { await compromisoApi.parentValidate(id, approve); toast.success(approve ? "Actividad validada." : "Actividad rechazada."); await load(); }
    catch (e) { toast.error(apiError(e)); }
    finally { setBusy(""); }
  };
  const validateSheet = async (id, approve) => {
    setBusy(id);
    try { const r = await compromisoApi.parentValidateSheet(id, approve); toast.success(approve ? `¡Hoja confirmada! +${r.puntos} pts` : "Entrega no validada."); await load(); }
    catch (e) { toast.error(apiError(e)); }
    finally { setBusy(""); }
  };

  const empty = !loading && data.actividades.length === 0 && data.hojas.length === 0;

  return (
    <div className="min-h-screen bg-cream" data-testid="validaciones-page">
      <header className="bg-navy">
        <div className="max-w-2xl mx-auto px-6 h-20 flex items-center justify-between">
          <button data-testid="val-back" onClick={() => navigate("/mi-cuenta")} className="flex items-center gap-1.5 text-white/70 hover:text-white text-sm font-semibold"><ArrowLeft className="w-4 h-4" /> Mi cuenta</button>
          <img src="/logo-supera-light.png" alt="Supera" className="h-11 w-auto object-contain" />
        </div>
      </header>

      <div className="max-w-2xl mx-auto px-6 py-8">
        <div className="flex items-center gap-3">
          <span className="w-12 h-12 rounded-xl bg-[var(--gold)] flex items-center justify-center"><ShieldCheck className="w-7 h-7 text-[#0A1C30]" /></span>
          <div><p className="text-xs font-extrabold tracking-[0.25em] text-[#B99B2E]">PADRES Y MADRES</p><h1 className="text-2xl font-extrabold text-[#0A1C30]">Validaciones Supera</h1></div>
        </div>
        <p className="mt-2 text-sm text-[#6B7688]">Confirma las actividades y entregas de tu hijo/a. Tu validación certifica el cumplimiento real de la metodología Supera.</p>

        <div className="mt-4 rounded-2xl bg-[#FFFBEB] ring-1 ring-[#E9A400]/30 p-4 flex items-start gap-3" data-testid="val-aviso-puntos">
          <ShieldCheck className="w-5 h-5 text-[#B9860B] flex-shrink-0 mt-0.5" />
          <p className="text-xs text-[#8A6D0B] leading-snug">Los puntos de graduación Supera se suman <b>solo</b> cuando la Hoja de Compromisos está cumplida al <b>100%</b>, validada por ti y, finalmente, aprobada por el <b>equipo Supera</b> en revisión. Al validar la entrega, la hoja se envía a revisión del equipo Supera.</p>
        </div>

        {loading && <div className="py-16 grid place-items-center"><Loader2 className="w-7 h-7 animate-spin text-[#E9A400]" /></div>}
        {empty && (
          <div className="mt-8 bg-white rounded-3xl ring-1 ring-black/5 p-10 text-center"><Inbox className="w-10 h-10 text-[#C7CDD6] mx-auto" /><p className="mt-3 text-[#6B7688] font-semibold">No tienes validaciones pendientes por ahora.</p></div>
        )}

        {/* Sheet validations first (higher importance) */}
        {data.hojas.map((h) => (
          <div key={h.id} className="mt-4 rounded-2xl bg-[#0A1C30] text-white p-5" data-testid={`sheet-val-${h.id}`}>
            <div className="flex items-center gap-2 text-[var(--gold)] text-xs font-extrabold"><ClipboardCheck className="w-4 h-4" /> ENTREGA DE HOJA SEMANAL</div>
            <p className="mt-2 text-sm leading-relaxed"><span className="font-extrabold">{h.student_name}</span> desea sumar su Hoja de Compromisos a los puntos Supera. ¿Deseas validar la entrega cumplida al 100%?</p>
            <p className="text-xs text-white/50 mt-1">Semana: {h.week_start}</p>
            <div className="mt-4 flex gap-2">
              <button data-testid={`sheet-approve-${h.id}`} disabled={busy === h.id} onClick={() => validateSheet(h.id, true)} className="flex-1 rounded-xl bg-[#22A860] font-extrabold py-2.5 inline-flex items-center justify-center gap-1.5 disabled:opacity-60"><Check className="w-4 h-4" /> Sí, valido</button>
              <button data-testid={`sheet-reject-${h.id}`} disabled={busy === h.id} onClick={() => validateSheet(h.id, false)} className="rounded-xl bg-white/10 hover:bg-white/20 font-bold py-2.5 px-4"><X className="w-4 h-4" /></button>
            </div>
          </div>
        ))}

        {/* Activity validations */}
        <div className="mt-4 space-y-3">
          {data.actividades.map((a) => (
            <div key={a.id} className="bg-white rounded-2xl ring-1 ring-black/5 p-4" data-testid={`act-val-${a.id}`}>
              <p className="text-sm text-[#2A3446] leading-relaxed">Tu hijo/a <span className="font-extrabold text-[#0A1C30]">{a.student_name}</span> desea validar como cumplida la actividad: <span className="font-bold">"{a.actividad}"</span>. ¿Validas esta actividad como cumplida?</p>
              <div className="mt-3 flex gap-2">
                <button data-testid={`act-approve-${a.id}`} disabled={busy === a.id} onClick={() => validate(a.id, true)} className="flex-1 rounded-xl bg-[#22A860] text-white font-extrabold py-2.5 inline-flex items-center justify-center gap-1.5 disabled:opacity-60"><Check className="w-4 h-4" /> Sí, valido</button>
                <button data-testid={`act-reject-${a.id}`} disabled={busy === a.id} onClick={() => validate(a.id, false)} className="rounded-xl bg-[#FDECEC] text-[#C0392B] font-bold py-2.5 px-4"><X className="w-4 h-4" /></button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
